
import Component from "vue-class-component";
import Vue from "vue";
import { ResultError, SysAdminTemplatesClient, TemplateLightModel, SysAdminTemplatesSortField } from "@/api-client";
import { BvTableCtxObject, BvTableFieldArray } from "bootstrap-vue";
import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
import HeadingWave from "@/features/shared/components/HeadingWave.vue";
import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
import { translateTrueFalse } from "@/features/shared/TrueFalseTranslator";
import ServersidePaging from "@/components/ServersidePaging";
import { debounceFn } from "debounce-decorator-ts";
import Loader from "@/features/shared/components/Loader";
import ButtonWithConfirm from "@/features/shared/components/ButtonWithConfirm.vue";
import toasts from "@/components/Toasts";
import ServersidePagingSelector from "@/components/ServersidePagingSelector.vue";
import FilterCheckbox from "@/components/FilterCheckbox.vue";


const isActiveString = "isActive";
const nameString = "name";

const defaultSortValue = SysAdminTemplatesSortField.NameAsc;

class Sort {
    value: SysAdminTemplatesSortField | null = defaultSortValue;

    constructor(value: SysAdminTemplatesSortField | null = defaultSortValue) {
        this.value = value;
    }

    get fieldName(): string | null {
        switch (this.value) {
            case SysAdminTemplatesSortField.ActiveAsc:
            case SysAdminTemplatesSortField.ActiveDesc:
                return isActiveString;
            case SysAdminTemplatesSortField.NameAsc:
            case SysAdminTemplatesSortField.NameDesc:
                return nameString;
            default:
                return null;
        }
    }

    get isSortedByDesc(): boolean {
        return this.value == null
            || this.value == SysAdminTemplatesSortField.ActiveDesc
            || this.value == SysAdminTemplatesSortField.NameDesc;
    }
}

@Component({
    components: {
        TableEmptySlot,
        HeadingWave,
        SectionPageHeading,
        ButtonWithConfirm,
        ServersidePagingSelector,
        FilterCheckbox
    },
})
export default class ApplicationTemplates extends Vue {
    templates: TemplateLightModel[] = [];

    loader = new Loader(true);
    sort = new Sort();
    paging = new ServersidePaging();
    searchPhrase = "";
    isActive = false;
    showAll = false;
    isDeactive = false;

    async created() {
        this.getParametersFromRouteOrDefault();

        this.$watch(() => this.searchPhrase, () => {
            this.paging.pageNumber = 1;
            this.reflectParametersToRoute();
        });

        this.$watch(() => this.isActive, () => {
            this.paging.pageNumber = 1;
            this.reflectParametersToRoute();
        });

        this.$watch(() => this.isDeactive, () => {
            this.paging.pageNumber = 1;
            this.reflectParametersToRoute();
        });

        this.$watch(() => this.showAll, () => {
            this.paging.pageNumber = 1;
            this.reflectParametersToRoute();
        });

        this.$watch(() => this.paging.pageNumber, () => {
            this.reflectParametersToRoute();
        });

        this.$watch(() => this.sort, () => {
            this.reflectParametersToRoute();
        }, { deep: true });

        this.$watch(() => this.$route.query, () => {
            this.getTemplates();
        }, { deep: true, immediate: true });
    }

    getTemplates() {
        this.templates = [];

        new SysAdminTemplatesClient()
            .setLoader(this.loader)
            .search(
                this.sort.value,
                this.isActive,
                this.isDeactive,
                this.showAll,
                this.paging.pageNumber,
                this.paging.pageSize,
                this.searchPhrase,
            )
            .then(queryResult => {
                this.templates = queryResult.pageResult;
                this.paging.totalCount = queryResult.totalCount;
                this.paging.pageSize = queryResult.pageSize;
            });
    }

    async deactivate(template: TemplateLightModel) {
        await new SysAdminTemplatesClient().deactivate(template.id).then(
            success => {
                template.isActive = false;
                this.reflectParametersToRoute();
                toasts.success(this.$ls.Administration_ApplicationTemplates_ApplicationTemplateDeactivated);
            },
            (error: ResultError) => error.messagesFlattened.forEach(x => toasts.failure(x)));
    }

    async activate(template: TemplateLightModel) {
        await new SysAdminTemplatesClient().activate(template.id).then(
            success => {
                template.isActive = true;
                this.reflectParametersToRoute();
                toasts.success(this.$ls.Administration_ApplicationTemplates_ApplicationTemplateActivated);
            },
            (error: ResultError) => error.messagesFlattened.forEach(x => toasts.failure(x)));
    }

    navigateToDetails(template: TemplateLightModel) {
        this.$router.push({
            name: "application-template",
            params: { id: template.id },
        });
    }

    @debounceFn(100) // Prevents possible double-update
    reflectParametersToRoute(): void {
        this.$router.replace({
            query: {
                // Setting to 'undefined' removes parameter from URL.
                searchPhrase: this.searchPhrase || undefined,
                pageNumber: this.paging.pageNumber ? this.paging.pageNumber.toString() : undefined,
                pageSize: this.paging.pageSize ? this.paging.pageSize.toString() : undefined,
                sort: this.sort.value || undefined,
                isActive: this.isActive.toString(),
                isDeactive: this.isDeactive.toString(),
                showAll: this.showAll.toString()
            }
        });
    }

    getParametersFromRouteOrDefault() {
        const route = this.$route as any;
        const query = route.query;
        this.searchPhrase = query.searchPhrase ? query.searchPhrase : '';
        this.paging.pageNumber = parseInt(query.pageNumber) || this.paging.pageNumber;
        this.paging.pageSize = parseInt(query.pageSize) || this.paging.pageSize;
        this.sort = new Sort(route.query.sort || defaultSortValue);
        this.isActive = query.isActive as boolean ?? true;
        this.isDeactive = query.isDeactive as boolean ?? false;
        this.showAll = query.showAll as boolean ?? false;
    }

    updateSort(ctx: BvTableCtxObject) {
        switch (ctx.sortBy) {
            case nameString:
                this.sort = new Sort(ctx.sortDesc ? SysAdminTemplatesSortField.NameDesc : SysAdminTemplatesSortField.NameAsc);
                break;
            case isActiveString:
                this.sort = new Sort(ctx.sortDesc ? SysAdminTemplatesSortField.ActiveDesc : SysAdminTemplatesSortField.ActiveAsc);
                break;
        }
    }

    fields: BvTableFieldArray = [
        {
            key: nameString,
            label: this.$ls.Global_Label_Name,
            thClass: "w-75",
            sortable: true
        },
        {
            key: isActiveString,
            label: this.$ls.Global_Label_Active,
            formatter: (value: any, key: any, item: TemplateLightModel): string => translateTrueFalse(item.isActive ?? false),
            sortable: true
        },
        {
            key: "actions",
            label: "",
            headerTitle: "actions",
            sortable: false,
            tdClass: "table-actions"
        },
        {
            key: "linkIcon",
            label: "",
            sortable: false,
        },
    ];
}
